import * as THREE from "three";
import { sparkleMaterial } from "./rareShiny";

/**
 * The Starfall Wyrm's comet trail (M40): glitter shed from its tail star and its wingtips as it flies — lilac,
 * rose-gold, cyan and white sparks that hang in the air, drift down and twinkle out — so the beast draws a ribbon of
 * stars along the road, up into the sky as it flees, and down as it falls. It sits on top of the rare's shine
 * (rareShiny.ts: the body's sheen, glow pulse and orbiting motes). One `THREE.Points` draw, `Count` pooled sprites in a
 * ring buffer, no per-frame allocation; nothing under reduced effects.
 */
export class WyrmTrail {
  static readonly Count = 220;
  static readonly Life = 1.7;
  /** Sparks a second from the tail, and from each wingtip (half while it hovers in place). */
  static readonly TailRate = 42;
  static readonly WingRate = 16;
  /** World size of a fresh spark (cells). */
  static readonly Size = 0.11;

  readonly points: THREE.Points;
  private readonly geo = new THREE.BufferGeometry();
  private readonly mat = sparkleMaterial();
  private readonly pos = new Float32Array(WyrmTrail.Count * 3);
  private readonly col = new Float32Array(WyrmTrail.Count * 3);
  private readonly size = new Float32Array(WyrmTrail.Count);
  private readonly born = new Float32Array(WyrmTrail.Count).fill(-99);
  private readonly from = new Float32Array(WyrmTrail.Count * 3);
  private readonly drift = new Float32Array(WyrmTrail.Count * 3);
  private readonly hue = new Uint8Array(WyrmTrail.Count);
  private readonly palette: THREE.Color[];
  private next = 0;
  private time = 0;
  private readonly owed = [0, 0, 0];
  private readonly at = new THREE.Vector3();
  /** The model's tail star and wingtips (null when the model lacks them: the body stands in). */
  private emitters: (THREE.Object3D | null)[] = [];
  private body: THREE.Object3D | null = null;
  private on = false;

  constructor(colours: readonly number[]) {
    this.geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute("size", new THREE.BufferAttribute(this.size, 1).setUsage(THREE.DynamicDrawUsage));
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this.points = new THREE.Points(this.geo, this.mat);
    this.points.frustumCulled = false;
    this.points.renderOrder = 4;
    this.points.visible = false;
    this.palette = [...colours, 0xffffff].map((c) => new THREE.Color(c));
  }

  /** The Wyrm appeared: shed from these nodes of its model (tail star, left and right wingtips) and its body. */
  begin(body: THREE.Object3D, tail: THREE.Object3D | null, wingL: THREE.Object3D | null, wingR: THREE.Object3D | null): void {
    this.body = body; this.emitters = [tail, wingL, wingR];
    this.born.fill(-99); this.owed.fill(0);
    this.on = true;
  }

  /** It is gone (dissolved, flown out of sight): the last sparks fade on their own, nothing new is shed. */
  end(): void { this.on = false; this.body = null; this.emitters = []; }

  /** Per frame: shed at `rate` (0..1: its presence), move and fade every live spark. */
  frame(dt: number, rate: number, moving: boolean, reduced: boolean): void {
    this.time += dt;
    if (reduced) { this.points.visible = false; return; }
    if (this.on && this.body && rate > 0.05) {
      for (let e = 0; e < 3; e++) {
        const per = (e === 0 ? WyrmTrail.TailRate : WyrmTrail.WingRate) * rate * (moving ? 1 : 0.5);
        this.owed[e] += per * dt;
        while (this.owed[e] >= 1) { this.owed[e] -= 1; this.emit(this.emitters[e] ?? this.body, e === 0); }
      }
    }
    let any = false;
    const t = this.time;
    for (let i = 0; i < WyrmTrail.Count; i++) {
      const age = t - this.born[i];
      if (age < 0 || age > WyrmTrail.Life) { this.size[i] = 0; continue; }
      any = true;
      const u = age / WyrmTrail.Life;
      this.pos[i * 3] = this.from[i * 3] + this.drift[i * 3] * age;
      this.pos[i * 3 + 1] = this.from[i * 3 + 1] + this.drift[i * 3 + 1] * age - 0.12 * age * age;
      this.pos[i * 3 + 2] = this.from[i * 3 + 2] + this.drift[i * 3 + 2] * age;
      const tw = 0.55 + 0.45 * Math.sin(t * 19 + i * 2.7);
      const k = (1 - u) * (1 - u) * tw * 1.9, c = this.palette[this.hue[i]];
      this.col[i * 3] = c.r * k; this.col[i * 3 + 1] = c.g * k; this.col[i * 3 + 2] = c.b * k;
      this.size[i] = WyrmTrail.Size * (1 - 0.55 * u) * (0.7 + 0.5 * tw);
    }
    this.points.visible = any;
    if (!any) return;
    (this.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.color as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.attributes.size as THREE.BufferAttribute).needsUpdate = true;
  }

  private emit(node: THREE.Object3D, tail: boolean): void {
    const i = this.next; this.next = (this.next + 1) % WyrmTrail.Count;
    node.getWorldPosition(this.at);
    const r = tail ? 0.12 : 0.06;
    this.from[i * 3] = this.at.x + (Math.random() - 0.5) * r;
    this.from[i * 3 + 1] = this.at.y + (Math.random() - 0.5) * r;
    this.from[i * 3 + 2] = this.at.z + (Math.random() - 0.5) * r;
    // spread wide and sinking: a slow flyer's sparks must stream off it, not pile up into a haze over its silhouette
    this.drift[i * 3] = (Math.random() - 0.5) * 0.5;
    this.drift[i * 3 + 1] = -0.15 + Math.random() * 0.2;
    this.drift[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
    this.hue[i] = Math.floor(Math.random() * this.palette.length);
    this.born[i] = this.time;
  }

  dispose(): void { this.points.removeFromParent(); this.geo.dispose(); this.mat.dispose(); }
}
